import type { RuntimeWalletTransaction } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { addDecimals, isPositiveDecimal, MONEY_INPUT_PATTERN } from "@/shared/utils/decimal";

import {
  WALLET_TOP_UP_LEDGER_DESCRIPTION,
  WALLET_TOP_UP_REFUND_LEDGER_DESCRIPTION,
} from "../constants/walletTopUps.constants";
import type { CreateWalletTopUpPayload } from "../types/walletTopUps.types";

/**
 * A statement leg that is a Coloca top-up: the runtime posts it as `ADJUST` with this exact description. A
 * refund for a payment incident is a top-up too (same receipt, read the same way), described `coloca
 * refund`.
 */
export function isColocaTopUp(
  transaction: Pick<RuntimeWalletTransaction, "operation_type" | "description">,
): boolean {
  const description = transaction.description?.trim();
  return (
    transaction.operation_type === "ADJUST" &&
    (description === WALLET_TOP_UP_LEDGER_DESCRIPTION ||
      description === WALLET_TOP_UP_REFUND_LEDGER_DESCRIPTION)
  );
}

/** A Coloca top-up that refunds a payment that failed at the provider. */
export function isColocaRefund(
  transaction: Pick<RuntimeWalletTransaction, "operation_type" | "description">,
): boolean {
  return (
    transaction.operation_type === "ADJUST" &&
    transaction.description?.trim() === WALLET_TOP_UP_REFUND_LEDGER_DESCRIPTION
  );
}

export type TopUpAmountProblem = "invalid" | "not_positive" | "precision";

/** Mirrors the backend: a positive decimal string, at most as many decimals as the currency settles in. */
export function topUpAmountProblem(amount: string, currency: string): TopUpAmountProblem | null {
  const value = amount.trim();
  if (!MONEY_INPUT_PATTERN.test(value)) return "invalid";
  if (!isPositiveDecimal(value)) return "not_positive";
  const decimals = value.split(".")[1]?.replace(/0+$/, "") ?? "";
  return decimals.length > minorUnitsFor(currency) ? "precision" : null;
}

export function balanceAfterTopUp(balance: string, amount: string): string {
  return addDecimals(balance, amount);
}

export function buildTopUpPayload(
  input: Omit<CreateWalletTopUpPayload, "amount" | "concept" | "internal_note"> & {
    amount: string;
    concept: string;
    internal_note: string;
  },
): CreateWalletTopUpPayload {
  return {
    organization_id: input.organization_id,
    wallet_id: input.wallet_id,
    amount: input.amount.trim(),
    concept: input.concept.trim(),
    internal_note: input.internal_note.trim(),
    idempotency_key: input.idempotency_key,
  };
}
